import { releaseAudioSession } from "@/lib/mic";
// One continuous soundscape for an Immersion: coach voice over music over binaural, all in one AudioContext.
// Music loops gaplessly (decoded buffer), the voice ducks the music softly, and nothing ever starts or stops abruptly.
const BASE = process.env.REACT_APP_BACKEND_URL;

async function decode(ctx, url) {
  const buf = await (await fetch(`${BASE}${url}`)).arrayBuffer();
  return await ctx.decodeAudioData(buf);
}

// A looping silent clip, started on the tap, moves iOS into "playback" so the silent switch can't mute Web Audio.
function silentWavUrl() {
  const n = 8000, buf = new ArrayBuffer(44 + n * 2), v = new DataView(buf);
  const w = (o, s) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  w(0, "RIFF"); v.setUint32(4, 36 + n * 2, true); w(8, "WAVEfmt "); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, 8000, true); v.setUint32(28, 16000, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); w(36, "data"); v.setUint32(40, n * 2, true);
  return URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
}

function unlockIOS() {
  try { if (navigator.audioSession) navigator.audioSession.type = "playback"; } catch {}
  const el = new Audio(silentWavUrl()); el.loop = true; el.setAttribute("playsinline", ""); el.play().catch(() => {});
  return el;
}

export function createMix({ musicUrl, binaural, musicGain = 0.42, duckGain = 0.14 }) {
  const keepAlive = unlockIOS();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  ctx.resume?.();
  const master = ctx.createGain(); master.gain.value = 1; master.connect(ctx.destination);
  const music = ctx.createGain(); music.gain.value = 0; music.connect(master);
  const voice = ctx.createGain(); voice.gain.value = 0.95; voice.connect(master);
  const tones = ctx.createGain(); tones.gain.value = 0; tones.connect(master);
  // whispers sit muffled and far back, under the music
  const whisper = ctx.createGain(); whisper.gain.value = 0;
  const muffle = ctx.createBiquadFilter(); muffle.type = "lowpass"; muffle.frequency.value = 2400;
  whisper.connect(muffle); muffle.connect(master);
  const buffers = {};
  const nodes = [];
  let ducks = 0, faded = false;

  const ramp = (g, v, sec) => { const t = ctx.currentTime; g.gain.cancelScheduledValues(t); g.gain.setValueAtTime(g.gain.value, t); g.gain.linearRampToValueAtTime(v, t + sec); };

  return {
    ctx,
    async load(urls) {
      await ctx.resume?.();
      await Promise.allSettled([musicUrl && decode(ctx, musicUrl).then((b) => { buffers.music = b; }), ...urls.map((u) => decode(ctx, u).then((b) => { buffers[u] = b; }))].filter(Boolean));
      await ctx.resume?.();
    },
    start() {
      if (buffers.music) { const src = ctx.createBufferSource(); src.buffer = buffers.music; src.loop = true; src.connect(music); src.start(); nodes.push(src); ramp(music, musicGain, 6); }
      if (binaural) {
        const merger = ctx.createChannelMerger(2); merger.connect(tones);
        [binaural.base, binaural.base + binaural.beat].forEach((f, ch) => { const o = ctx.createOscillator(); o.frequency.value = f; o.connect(merger, 0, ch); o.start(); nodes.push(o); });
        ramp(tones, binaural.gain, 12);
      }
    },
    // the coach speaks: music dips gently under her, then rises back
    speak(url) {
      const b = buffers[url]; if (!b) return Promise.resolve();
      ducks += 1; ramp(music, duckGain, 1.4);
      const src = ctx.createBufferSource(); src.buffer = b; src.connect(voice); src.start(ctx.currentTime + 0.3); nodes.push(src);
      // timed from the clip length, so the words only arrive once she has finished
      return new Promise((res) => setTimeout(() => { ducks -= 1; if (!ducks && !faded) ramp(music, musicGain, 3.5); res(); }, (b.duration + 0.5) * 1000));
    },
    whisper(url, gain) {
      const b = buffers[url]; if (!b) return;
      const src = ctx.createBufferSource(); src.buffer = b; src.loop = true; src.playbackRate.value = 0.94; src.connect(whisper); src.start(); nodes.push(src);
      ramp(whisper, gain, 10);
    },
    setWhisper(gain, sec = 8) { ramp(whisper, gain, sec); },
    setMuted(m) { ramp(master, m ? 0 : 1, 0.4); },
    fadeOut(sec = 6) { faded = true; ramp(music, 0, sec); ramp(tones, 0, sec); ramp(whisper, 0, sec); },
    close() { keepAlive.pause(); releaseAudioSession(); nodes.forEach((n) => { try { n.stop(); } catch {} }); if (ctx.state !== "closed") ctx.close().catch(() => {}); },
  };
}
